///
/// EarthBrowser
///
/// Created by Matt Giger on 11/17/2025
/// Copyright (c) 2025 Geodata Labs
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { PolylineGeometry, type PolylineOptions } from "@/earthbrowser/entities/Polyline/PolylineGeometry"
import { PolylineRenderer, type PolylineMaterial } from "@/earthbrowser/entities/Polyline/PolylineRenderer"
import { type color, rgba } from "@stovokor/math/color"
import { type vec3 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"

export interface PolylineNodeOptions extends PolylineOptions {
	color?: color
	/** Number of arrow markers along the line (0 = no arrows) */
	arrowCount?: number
	/** Speed of arrow animation (default 0.5) */
	arrowSpeed?: number
	/** Enable depth write (default true). Disable for overlays that should always be visible */
	depthWrite?: boolean
	/** Depth bias [factor, units] toward the camera for lines draped on the terrain (see surfaceDepthBias) */
	polygonOffset?: [number, number]
}

/**
 * Material that includes arrow animation properties
 */
class PolylineNodeMaterial implements PolylineMaterial {
	public color: color
	public transparency: boolean
	public available = true
	public arrowCount: number
	public arrowSpeed: number

	constructor(color: color, arrowCount: number = 0, arrowSpeed: number = 0.5) {
		this.color = color
		this.transparency = color.a < 1.0
		this.arrowCount = arrowCount
		this.arrowSpeed = arrowSpeed
	}
}

/**
 * A node that renders a polyline with screen-space constant width
 * Supports animated arrow patterns flowing along the line
 *
 * Example usage:
 * ```typescript
 * const line = new PolylineNode({
 *   positions: [
 *     new vec3(0, 0, 0),
 *     new vec3(1, 1, 0),
 *     new vec3(2, 0, 0)
 *   ],
 *   width: 3,
 *   color: rgba(1, 0, 0, 1),
 *   arrowCount: 5,  // 5 arrows along the line
 *   arrowSpeed: 0.3 // slow animation
 * })
 * ```
 */
export class PolylineNode extends NodeBase {
	private polyline: PolylineGeometry
	private material: PolylineNodeMaterial
	private context: StovokorContext
	private depthWrite: boolean
	private polygonOffset?: [number, number]

	constructor(context: StovokorContext, options: PolylineNodeOptions) {
		super()
		this.context = context
		this.depthWrite = options.depthWrite ?? true
		this.polygonOffset = options.polygonOffset

		const lineColor = options.color ?? rgba(1, 1, 1, 1)
		this.polyline = new PolylineGeometry(context, options)
		this.material = new PolylineNodeMaterial(
			lineColor,
			options.arrowCount ?? 0,
			options.arrowSpeed ?? 0.5
		)
		this.material.transparency = this.isTransparent(lineColor)

		this.renderer = new PolylineRenderer(context, this.polyline, this.material, this.depthWrite, this.polygonOffset)
	}

	/**
	 * Update the polyline positions
	 *
	 * When the position count is unchanged this refills the existing GPU buffer
	 * in place (cheap enough to call every frame). Otherwise the buffers and
	 * renderer are recreated.
	 */
	public updatePositions(positions: vec3[], loop: boolean = false): void {
		const buffersRecreated = this.polyline.updatePositions(positions, loop)
		if(buffersRecreated) {
			// The renderer's bind actions capture buffer references, so it must be rebuilt
			this.renderer = new PolylineRenderer(this.context, this.polyline, this.material, this.depthWrite, this.polygonOffset)
		}
	}

	/**
	 * Update the line width
	 */
	public updateWidth(width: number): void {
		this.polyline.updateWidth(width)
	}

	/**
	 * Update the line color
	 */
	public updateColor(newColor: color): void {
		this.material.color = newColor
		this.material.transparency = this.isTransparent(newColor)
	}

	/**
	 * A line that doesn't write depth can't render reliably in the opaque
	 * pass: whether it survives depends on its sort order against
	 * depth-writing opaque geometry (earth tiles render at depth 0, and this
	 * node's world origin can jitter around 0 with floating-point noise, e.g.
	 * storm track overlays parented with an inverse transform). Force such
	 * overlay lines into the transparent pass so they always draw after
	 * opaque geometry, even when their color alpha reaches 1.
	 */
	private isTransparent(lineColor: color): boolean {
		return !this.depthWrite || lineColor.a < 1.0
	}

	/**
	 * Update arrow animation settings
	 */
	public updateArrows(arrowCount: number, arrowSpeed: number = 0.5): void {
		this.material.arrowCount = arrowCount
		this.material.arrowSpeed = arrowSpeed
	}

	override destroy(): void {
		this.polyline.destroy()
		super.destroy()
	}
}
